// Single source of truth for all theme values
// Used by both vanilla-extract (theme.css.ts) and runtime components

export const themeValues = {
  colors: {
    primary: {
      lightest: "#e5a1a4",
      lighter: "#db6e72",
      light: "#d13f45",
      default: "#c8131b",
      dark: "#94090f",
      darker: "#610206",
    },
    red: {
      lightest: "#e5a1a4",
      lighter: "#db6e72",
      light: "#d13f45",
      default: "#c8131b",
      dark: "#94090f",
      darker: "#610206",
    },
    yellow: {
      lightest: "#ffebc8",
      lighter: "#ffd894",
      light: "#ffc561",
      default: "#ffb32f",
      dark: "#cc8c1d",
      darker: "#99670f",
    },
    green: {
      lightest: "#bce3de",
      lighter: "#89d9cf",
      light: "#59cfc1",
      default: "#2ec9b7",
      dark: "#1d9688",
      darker: "#0f635a",
    },
    blue: {
      lightest: "#99dfff",
      lighter: "#66cfff",
      light: "#33bfff",
      default: "#01afff",
      dark: "#018ccc",
      darker: "#016999",
    },
    orange: {
      lightest: "#FFD8BD",
      lighter: "#FFB98A",
      light: "#FF9B57",
      default: "#FF7D24",
      dark: "#CC5F14",
      darker: "#994409",
    },
    second_green: {
      lightest: "#A5C7AF",
      lighter: "#77BD8B",
      light: "#4DB26A",
      default: "#27A84C",
      dark: "#167532",
      darker: "#0A421A",
    },
    neutral: {
      white: "#ffffff",
      lightest: "#f6f6f6",
      lighter: "#ededed",
      light: "#c6c6c6",
      default: "#7a7a7a",
      dark: "#545454",
      darker: "#222222",
      black: "#000000",
    },
    fixed: {
      white: "#ffffff",
      black: "#000000",
    },
  },
  shadows: {
    lighter: "0px 0px 2px 0px #0000001F, 0px 1px 2px 0px #00000024;",
    light: "0px 0px 2px 0px #0000001F, 0px 2px 4px 0px #00000024;",
    default: "0px 0px 2px 0px #0000001F, 0px 4px 8px 0px #00000024;",
    dark: "0px 0px 2px 0px #0000001F, 0px 8px 16px 0px #00000024;",
    darker: "0px 0px 8px 0px #00000033, 0px 10px 25px 0px #00000024;",
  },
  typography: {
    family: {
      default: '"Poppins", sans-serif',
    },
    weights: {
      regular: "400",
      medium: "500",
      semibold: "600",
      bold: "700",
    },
    scale: {
      display: { fontSize: "24px", lineHeight: "28px", fontWeight: "700" },
      heading: { fontSize: "20px", lineHeight: "24px", fontWeight: "700" },
      body: { fontSize: "14px", lineHeight: "20px", fontWeight: "400" },
      caption: { fontSize: "12px", lineHeight: "16px", fontWeight: "500" },
      small: { fontSize: "10px", lineHeight: "14px", fontWeight: "400" },
    },
  },
  spacing: {
    xxs: "4px",
    xs: "8px",
    s: "16px",
    m: "24px",
    l: "32px",
    xl: "40px",
    xxl: "48px",
  },
  radius: {
    s: "4px",
    m: "8px",
    l: "12px",
    xl: "16px",
    xxl: "24px",
  },
  semantic: {
    error: "#c8131b",
    success: "#2ec9b7",
    info: "#01afff",
    warning: "#ffb32f",
  },
  zIndex: {
    dropdown: "1000",
    modal: "1100",
    tooltip: "1200",
    toast: "1300",
  },
  motion: {
    fast: "150ms",
    normal: "300ms",
    slow: "500ms",
  },
  components: {
    fieldInfo: {
      fontSize: {
        desktop: "12px",
        tablet: "14px",
        totem: "16px",
      },
    },
    input: {
      label: {
        fontSize: {
          desktop: "16px",
          large: "16px",
          medium: "14px",
          small: "12px",
          tablet: "18px",
          "tablet-medium": "16px",
          "tablet-small": "16px",
          totem: "20px",
          "totem-medium": "18px",
          "totem-small": "16px",
        },
      },
      input: {
        fontSize: {
          desktop: "14px",
          large: "14px",
          medium: "14px",
          small: "12px",
          tablet: "16px",
          "tablet-medium": "16px",
          "tablet-small": "16px",
          totem: "18px",
          "totem-medium": "16px",
          "totem-small": "16px",
        },
        height: {
          desktop: "40px",
          large: "40px",
          medium: "32px",
          small: "24px",
          tablet: "48px",
          "tablet-medium": "40px",
          "tablet-small": "32px",
          totem: "56px",
          "totem-medium": "48px",
          "totem-small": "40px",
        },
        borderRadius: {
          desktop: "8px",
          large: "8px",
          medium: "8px",
          small: "4px",
          tablet: "12px",
          "tablet-medium": "8px",
          "tablet-small": "4px",
          totem: "16px",
          "totem-medium": "8px",
          "totem-small": "8px",
        },
        padding: {
          desktop: "8px 16px",
          large: "8px 16px",
          medium: "4px 12px",
          small: "4px 8px",
          tablet: "8px 16px",
          "tablet-medium": "4px 16px",
          "tablet-small": "4px 12px",
          totem: "8px 12px",
          "totem-medium": "8px 12px",
          "totem-small": "4px 12px",
        },
      },
    },
    select: {
      padding: {
        desktop: "4px 16px",
        tablet: "4px 16px",
        totem: "4px 12px",
      },
    },
    button: {
      fontSize: {
        medium: "14px",
        small: "12px",
        large: "14px",
        tablet: "16px",
        "tablet-medium": "16px",
        "tablet-small": "14px",
        totem: "24px",
        "totem-medium": "24px",
        "totem-small": "22px",
      },
      height: {
        large: "40px",
        medium: "32px",
        small: "24px",
        tablet: "48px",
        "tablet-medium": "40px",
        "tablet-small": "32px",
        totem: "56px",
        "totem-medium": "48px",
        "totem-small": "40px",
      },
      padding: {
        large: {
          text: "0 24px",
          icon: "0 24px 0 16px",
          textAndIcon: "0 24px 0 16px",
        },
        medium: {
          text: "0 12px",
          icon: "0 12px 0 8px",
          textAndIcon: "0 12px 0 8px",
        },
        small: {
          text: "0 8px",
          icon: "0 8px 0 4px",
          textAndIcon: "0 8px 0 4px",
        },
        tablet: {
          text: "0 24px",
          icon: "0 24px 0 16px",
          textAndIcon: "0 24px 0 16px",
        },
        "tablet-medium": {
          text: "0 12px",
          icon: "0 12px 0 8px",
          textAndIcon: "0 12px 0 8px",
        },
        "tablet-small": {
          text: "0 8px",
          icon: "0 8px 0 4px",
          textAndIcon: "0 8px 0 4px",
        },
        totem: {
          text: "0 24px",
          icon: "0 24px 0 16px",
          textAndIcon: "0 24px 0 16px",
        },
        "totem-medium": {
          text: "0 12px",
          icon: "0 12px 0 8px",
          textAndIcon: "0 12px 0 8px",
        },
        "totem-small": {
          text: "0 8px",
          icon: "0 8px 0 4px",
          textAndIcon: "0 8px 0 4px",
        },
      },
      borderRadius: {
        large: "12px",
        medium: "8px",
        small: "4px",
        tablet: "12px",
        "tablet-medium": "8px",
        "tablet-small": "4px",
        totem: "12px",
        "totem-medium": "8px",
        "totem-small": "8px",
      },
      iconSize: {
        large: "24px",
        medium: "20px",
        small: "16px",
        tablet: "24px",
        "tablet-medium": "20px",
        "tablet-small": "16px",
        totem: "36px",
        "totem-medium": "32px",
        "totem-small": "28px",
      },
    },
  },
  device: "desktop" as const,
} as const;

export type ThemeValues = typeof themeValues;
export type DeviceType = "desktop" | "tablet" | "totem";
